// Admin → Integrations → GitHub. Multi-slot (an org can wire several
// GitHub Projects v2 boards) with per-instance config + manual sync
// controls. Slots reuse the github-sync edge function (which already
// accepts an orgId via request body per migration 0053).

import {
  loadIntegrationCatalogue,
  saveIntegration,
  deleteIntegration,
  callIntegrationOp
} from '../../integrations/index.js'
// resolveScope is a shared-library function; invoke via el.call('resolveScope', draft, defaultScopeType).

const KIND = 'github'
const DEFAULT_SCOPE = 'org'

const _loadInstances = async (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) {
    if (!root._githubHubWatcher) {
      root._githubHubWatcher = setInterval(() => {
        if (root?.jwtClaims?.workspace_org_id) {
          clearInterval(root._githubHubWatcher)
          root._githubHubWatcher = null
          _loadInstances(root)
        }
      }, 300)
    }
    return
  }
  const data = await loadIntegrationCatalogue(orgId)
  root.update({ _integrationHub: data })
}

const _triggerSync = async (root, slug) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) return { ok: false, error: 'no active workspace' }
  // TODO[sdk-edge-invoke]: github-sync is a governance-tier Supabase edge
  // function (not a workspace wrapper route). The SDK has no flat method
  // for invoking arbitrary edge functions on the governance project; once
  // a /workspace/integrations/sync admin route or a callIntegrationOp
  // 'sync' op ships, replace with
  // `getSDK().execute('workspaceProject.integrations', 'sync', {...})`.
  // For now, surface a clear error rather than silently dropping the call.
  return {
    ok: false,
    error: 'github-sync edge function invocation not yet routed through SDK; ' +
      'pending wrapper /integrations/sync endpoint'
  }
}

const _slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '')
    .slice(0, 40) || 'default'

export const adminIntegrationsGithub = {
  extends: 'AppPage',
  metadata: { title: 'GitHub / Integrations / Symbols Workspace' },
  onInit: (el, s) => {
    if (!el.call('shouldRedirectNonAdmin', s)) return
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/', root, {}, { pushState: true })
  },
  onCreate: (el, s) => _loadInstances(s.root),

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: {
        BackLink: {
          tag: 'button',
          type: 'button',
          padding: '0',
          background: 'transparent',
          border: 'none',
          color: 'caption',
          fontSize: 'Z2',
          cursor: 'pointer',
          text: '← Integrations',
          onClick: (e, el, s) => {
            const root = el.call('findRouteRoot', el)
            if (root) el.router('/admin/integrations', root)
          }
        },
        Title: { text: 'GitHub' }
      },
      RightGroup: {
        AddBtn: {
          tag: 'button',
          type: 'button',
          padding: 'Z A',
          round: 'Y',
          background: 'blue',
          color: 'white',
          fontSize: 'Z2',
          fontWeight: '700',
          cursor: 'pointer',
          border: 'none',
          text: '+ Add a Projects board',
          onClick: (e, el, s) => {
            s.root.update({
              _newGithubDraft: {
                slug: '', displayName: '', owner: '', projectId: '', token: ''
              },
              _githubFormOpen: true
            })
          }
        }
      }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'A',

      Hint: {
        tag: 'p',
        fontSize: 'Z2',
        color: 'caption',
        margin: '0',
        text: 'Tickets in governance stay in sync with GitHub Projects v2. Every board below is a separate slot — add one per team, per product, or per org you want to mirror.'
      },

      NewForm: { extends: 'GithubNewForm' },
      Instances: { extends: 'GithubInstanceList' }
    }
  }
}

export const GithubNewForm = {
  hide: (el, s) => !s.root?._githubFormOpen,
  flow: 'y',
  gap: 'Z1',
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'blue',
  padding: 'A',

  Title: {
    tag: 'h3', fontSize: 'A', fontWeight: '700', color: 'title', margin: '0',
    text: 'Add a GitHub Projects v2 board'
  },

  Fields: {
    flow: 'y',
    gap: 'Z1',
    childExtends: 'GithubField',
    childrenAs: 'state',
    children: () => [
      { label: 'Display name', placeholder: 'symbols.app board', draftKey: 'displayName' },
      { label: 'Slug', placeholder: 'symbols-main', draftKey: 'slug' },
      { label: 'Repo owner / org (required)', placeholder: 'symbo-ls', draftKey: 'owner' },
      { label: 'Projects v2 ID (required)', placeholder: '5', draftKey: 'projectId' }
    ]
  },

  ScopePicker: {
    extends: 'IntegrationScopePicker',
    draftKey: '_newGithubDraft',
    defaultScopeType: DEFAULT_SCOPE
  },

  Token: {
    flow: 'y',
    gap: 'V',
    Label: {
      tag: 'label', fontSize: 'Y2', color: 'caption', fontWeight: '700',
      text: 'Personal access token (required — written to Vault)'
    },
    Textarea: {
      tag: 'textarea',
      rows: '3',
      width: '100%',
      padding: 'Z',
      round: 'Y',
      background: 'document',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontFamily: 'mono',
      fontSize: 'Z2',
      placeholder: 'ghp_... / github_pat_...',
      onInput: (e, el, s) => {
        s.root.update({
          _newGithubDraft: {
            ...(s.root?._newGithubDraft || {}),
            token: e.target.value
          }
        })
      }
    },
    Hint: {
      tag: 'p', fontSize: 'Y2', color: 'caption', margin: '0',
      text: 'Classic: read:project, project, repo. Fine-grained: Projects (Read/Write) at the org level.'
    }
  },

  Actions: {
    flow: 'x',
    gap: 'Z',
    align: 'center flex-end',
    marginTop: 'Z',

    CancelBtn: {
      tag: 'button', type: 'button',
      padding: 'V Z', round: 'Y',
      background: 'transparent',
      borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
      color: 'title', cursor: 'pointer', fontSize: 'Y2',
      text: 'Cancel',
      onClick: (e, el, s) => {
        s.root.update({ _githubFormOpen: false, _newGithubDraft: null })
      }
    },

    SaveBtn: {
      tag: 'button', type: 'button',
      padding: 'V A', round: 'Y',
      background: 'blue', color: 'white',
      fontSize: 'Y2', fontWeight: '700',
      cursor: 'pointer', border: 'none',
      text: (el, s) => (s.root?._githubSaving ? 'Saving…' : 'Connect'),
      onClick: async (e, el, s) => {
        const orgId = s.root?.jwtClaims?.workspace_org_id
        const draft = s.root?._newGithubDraft || {}
        if (!orgId) {
          el.call('openNotification', { title: 'No active workspace', type: 'error' })
          return
        }
        if (!draft.owner || !draft.projectId || !draft.token) {
          el.call('openNotification', {
            title: 'Missing required fields',
            message: 'Owner, project ID, and token are required.',
            type: 'error'
          })
          return
        }
        const slug = _slugify(draft.slug || draft.displayName || `${draft.owner}-${draft.projectId}`)
        const scope = el.call('resolveScope', draft, DEFAULT_SCOPE)
        if (scope.scopeType !== 'org' && !scope.scopeId) {
          el.call('openNotification', {
            title: 'Scope target required',
            message: `Select a ${scope.scopeType} or switch scope to Org.`, type: 'error'
          })
          return
        }
        s.root.update({ _githubSaving: true })
        const { ok, body } = await saveIntegration({
          orgId, kind: KIND, slug,
          scopeType: scope.scopeType, scopeId: scope.scopeId,
          displayName: draft.displayName || `${draft.owner} / ${draft.projectId}`,
          secret: draft.token,
          config: {
            owner: draft.owner,
            project_id: draft.projectId,
            display_name: draft.displayName || `${draft.owner} / ${draft.projectId}`
          },
          enabled: true
        })
        s.root.update({ _githubSaving: false })
        if (ok) {
          s.root.update({ _githubFormOpen: false, _newGithubDraft: null })
          await _loadInstances(s.root)
          el.call('openNotification', { title: 'Connected', type: 'success' })
        } else {
          el.call('openNotification', {
            title: 'Connect failed',
            message: body?.error || 'Server error',
            type: 'error'
          })
        }
      }
    }
  }
}

export const GithubField = {
  flow: 'y', gap: 'V',
  Label: {
    tag: 'label', fontSize: 'Y2', color: 'caption', fontWeight: '700',
    text: (el, s) => s.label
  },
  Input: {
    tag: 'input', type: 'text',
    width: '100%', padding: 'Z', round: 'Y',
    background: 'document',
    borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
    color: 'title', fontSize: 'Z2',
    placeholder: (el, s) => s.placeholder || '',
    value: (el, s) => {
      const draft = s.root?._newGithubDraft || {}
      return draft[s.draftKey] || ''
    },
    onInput: (e, el, s) => {
      s.root.update({
        _newGithubDraft: {
          ...(s.root?._newGithubDraft || {}),
          [s.draftKey]: e.target.value
        }
      })
    }
  }
}

export const GithubInstanceList = {
  flow: 'y', gap: 'A',
  childExtends: 'GithubInstanceCard',
  childrenAs: 'state',
  children: (el, s) => {
    const list = s.root?._integrationHub?.integrations || []
    return list.filter((i) => i.kind === KIND)
  },
  EmptyState: {
    hide: (el, s) => {
      const list = s.root?._integrationHub?.integrations || []
      return list.filter((i) => i.kind === KIND).length > 0
    },
    padding: 'B', round: 'Z1', background: 'overlay',
    borderWidth: '1px', borderStyle: 'dashed', borderColor: 'line',
    textAlign: 'center', color: 'caption', fontSize: 'Z2',
    text: 'No GitHub Projects boards connected. Add one to start syncing tickets.'
  }
}

export const GithubInstanceCard = {
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
  padding: 'A',
  flow: 'y', gap: 'Z1',

  TopRow: {
    flow: 'x', align: 'center space-between', gap: 'Z',

    Left: {
      flow: 'x', align: 'center', gap: 'Z1',
      Icon: { icon: 'github', boxSize: 'A' },
      Info: {
        flow: 'y', gap: 'V',
        Name: {
          tag: 'span', fontSize: 'A', fontWeight: '700', color: 'title',
          text: (el, s) => s.display_name || `${s.config?.owner || '?'} / ${s.config?.project_id || '?'}`
        },
        Meta: {
          tag: 'span', fontSize: 'Y2', color: 'caption',
          text: (el, s) => `slug: ${s.slug} · owner: ${s.config?.owner || '—'} · project: ${s.config?.project_id || '—'}`
        }
      }
    },

    Right: {
      flow: 'x', gap: 'Z', align: 'center',

      StatusPill: {
        tag: 'span', padding: 'V Z', round: 'Y',
        fontSize: 'Y1', fontWeight: '700',
        background: (el, s) => (s.enabled && s.has_secret ? 'green' : 'overlayStrong'),
        color: (el, s) => (s.enabled && s.has_secret ? 'white' : 'caption'),
        text: (el, s) => {
          if (!s.has_secret) return 'Missing token'
          if (!s.enabled) return 'Disabled'
          return 'Live'
        }
      },

      SyncBtn: {
        tag: 'button', type: 'button',
        padding: 'V Z', round: 'Y',
        background: 'blue', color: 'white',
        fontSize: 'Y2', fontWeight: '700',
        cursor: 'pointer', border: 'none',
        text: (el, s) => {
          const syncing = s.root?._githubSyncing === s.slug
          return syncing ? 'Syncing…' : 'Pull now'
        },
        onClick: async (e, el, s) => {
          s.root.update({ _githubSyncing: s.slug })
          const { ok, error } = await _triggerSync(s.root, s.slug)
          s.root.update({ _githubSyncing: null })
          if (ok) el.call('openNotification', { title: 'Sync started', type: 'success' })
          else el.call('openNotification', { title: 'Sync failed', message: error, type: 'error' })
        }
      },

      RemoveBtn: {
        tag: 'button', type: 'button',
        padding: 'V Z', round: 'Y',
        background: 'transparent',
        borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
        color: 'red', fontSize: 'Y2', cursor: 'pointer',
        text: 'Remove',
        onClick: async (e, el, s) => {
          if (!window.confirm(`Disconnect ${s.display_name || s.slug}? Tickets remain in governance but new sync stops.`)) return
          const orgId = s.root?.jwtClaims?.workspace_org_id
          if (!orgId) return
          const { ok, body } = await deleteIntegration({
            orgId, kind: KIND, slug: s.slug,
            scopeType: s.scope_type || 'org', scopeId: s.scope_id || null
          })
          if (ok) {
            await _loadInstances(s.root)
            el.call('openNotification', { title: 'Disconnected', type: 'success' })
          } else {
            el.call('openNotification', {
              title: 'Remove failed',
              message: body?.error || 'Server error',
              type: 'error'
            })
          }
        }
      }
    }
  }
}
